import React, { useState } from 'react';
import { useLanguage } from '../../../utils/i18n';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PlaceIcon from '@mui/icons-material/Place';
import dayjs from 'dayjs';

const formatDuration = (seconds) => {
  const secs = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const remainingSecs = secs % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(remainingSecs)}`;
};

export const getSpatialTypeColor = (spatialType) => {
  switch (String(spatialType).toLowerCase()) {
    case 'geofence':
      return { bgcolor: '#e3f2fd', color: '#1565c0', label: 'Geofence' };
    case 'pool':
      return { bgcolor: '#e8f5e9', color: '#2e7d32', label: 'Pool' };
    case 'transit_point':
    case 'transitpoint':
    case 'transit point':
      return { bgcolor: '#fff3e0', color: '#e65100', label: 'Transit Point' };
    default:
      return { bgcolor: '#f5f5f5', color: '#616161', label: spatialType || '-' };
  }
};

const getPolygonTypeDisplay = (item) => {
  const isTransit = item._filterType === 'transit_point' || item.spatial_type === 'transit_point';
  if (isTransit) {
    return '-';
  }
  const polyType = item.polygon_type_name || item.join?.polygon_type_name || '';
  if (typeof polyType === 'string' && polyType.trim() !== '') {
    return polyType;
  }
  return 'Unknown Type';
};

export default function RetaseDialog({ open, onClose, date, retaseTrips = [], geofences = [], transitPoints = [] }) {
  const { t } = useLanguage(); // RetaseDialog
  const [currentTab, setCurrentTab] = useState(0);

  const combinedSpatialData = React.useMemo(() => {
    const list = [
      ...geofences.map((g) => ({ ...g, _filterType: 'geofence' })),
      ...transitPoints.map((tp) => ({ ...tp, _filterType: 'transit_point' }))
    ];

    list.sort((a, b) => {
      const nameA = String(a.name || a.geofence_code || '').toLowerCase();
      const nameB = String(b.name || b.geofence_code || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return list;
  }, [geofences, transitPoints]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 3 } }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {t('driverScore.retaseHistory', 'Retase History')}
            </Typography>
            {date && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t('driverScore.dateLabel', 'Date')}: {date}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#ffffff' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="retase dialog tabs"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: 48,
              px: 2
            }
          }}
        >
          <Tab
            icon={<AltRouteIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{t('driverScore.retaseHistory', 'Retase History')}</span>
                <Chip
                  label={`${retaseTrips.length} ${t('driverScore.tripUnit', 'Trip')}`}
                  size="small"
                  sx={{
                    bgcolor: currentTab === 0 ? '#e8f5e9' : '#f1f5f9',
                    color: currentTab === 0 ? '#2e7d32' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 20
                  }}
                />
              </Stack>
            }
          />
          <Tab
            icon={<PlaceIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{t('driverScore.polygon', 'Polygon')}</span>
                <Chip
                  label={`${combinedSpatialData.length} ${t('driverScore.dataUnit', 'Data')}`}
                  size="small"
                  sx={{
                    bgcolor: currentTab === 1 ? '#e3f2fd' : '#f1f5f9',
                    color: currentTab === 1 ? '#1565c0' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 20
                  }}
                />
              </Stack>
            }
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 2, minHeight: 300 }}>
        {currentTab === 0 && (
          <Box>
            {retaseTrips.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('driverScore.noRetaseData', 'No retase data for this date')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, overflow: 'auto', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: 160 }}>
                        {t('driverScore.startTime', 'Start Time')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: 160 }}>
                        {t('driverScore.endTime', 'End Time')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: 100 }}>
                        {t('driverScore.duration', 'Duration')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>{t('driverScore.route', 'Route')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {retaseTrips.map((trip, idx) => {
                      const detail = trip.details?.[0] || {};
                      const startTime = detail.start ? dayjs(detail.start).format('DD MMM YYYY HH:mm') : '-';
                      const endTime = detail.end ? dayjs(detail.end).format('DD MMM YYYY HH:mm') : '-';
                      const durFormatted = detail.dur != null ? formatDuration(detail.dur) : '-';
                      const routes = Array.isArray(trip.routes)
                        ? [...trip.routes].sort((a, b) => (Number(a.sequence_number) || 0) - (Number(b.sequence_number) || 0))
                        : [];

                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{startTime}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{endTime}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{durFormatted}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                              {routes.map((r, rIdx) => {
                                const typeInfo = getSpatialTypeColor(r.spatial_type);
                                return (
                                  <Box key={rIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {rIdx > 0 && (
                                      <Typography variant="body2" sx={{ color: 'text.secondary', mx: 0.25, fontWeight: 'bold' }}>
                                        →
                                      </Typography>
                                    )}
                                    <Chip
                                      label={typeInfo.label}
                                      size="small"
                                      sx={{
                                        bgcolor: typeInfo.bgcolor,
                                        color: typeInfo.color,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight="500">
                                      {r.spatial_name || '-'}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {currentTab === 1 && (
          <Box>
            {combinedSpatialData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('driverScore.noPolygonData', 'No polygon data for this date')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, overflow: 'auto', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: 140 }}>{t('general.code', 'Code')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', minWidth: 160 }}>
                        {t('driverScore.locationName', 'Location Name')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: 160 }}>
                        {t('driverScore.polygonType', 'Polygon Type')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: 140 }}>
                        {t('driverScore.speedLimit', 'Speed Limit')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', minWidth: 180 }}>
                        {t('driverScore.addressRemark', 'Address / Remark')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {combinedSpatialData.map((item, idx) => {
                      const code = item.geofence_code || item.code || '-';
                      const locationName = item.name || item.spatial_name || '-';
                      const polygonType = getPolygonTypeDisplay(item);
                      const speedLimit =
                        item.max_speed != null
                          ? `${item.min_speed ?? 0} - ${item.max_speed} ${t('driverScore.kmPerHour', 'km/h')}`
                          : item.remark || '-';
                      const addressOrRemark = item.address || item.remark || (item.pic_name ? `PIC: ${item.pic_name}` : '-');

                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{code}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{locationName}</TableCell>
                          <TableCell>
                            {polygonType === '-' ? (
                              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                -
                              </Typography>
                            ) : polygonType === 'Unknown Type' ? (
                              <Chip
                                label={t('driverScore.unknownType', 'Unknown Type')}
                                size="small"
                                sx={{
                                  bgcolor: '#f1f5f9',
                                  color: '#64748b',
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  height: 22
                                }}
                              />
                            ) : (
                              <Chip
                                label={polygonType}
                                size="small"
                                sx={{
                                  bgcolor: '#e3f2fd',
                                  color: '#1565c0',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 22,
                                  textTransform: 'capitalize'
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{speedLimit}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{addressOrRemark}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {currentTab === 0
            ? `${t('driverScore.totalLabel', 'Total')}: ${retaseTrips.length} ${t('driverScore.tripUnit', 'Trip')}`
            : `${t('driverScore.totalLabel', 'Total')}: ${combinedSpatialData.length} ${t('driverScore.dataUnit', 'Data')}`}
        </Typography>
        <Button onClick={onClose} variant="outlined" size="small" color="inherit">
          {t('general.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RetaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string,
  retaseTrips: PropTypes.array,
  geofences: PropTypes.array,
  transitPoints: PropTypes.array
};
